import {
  Alert,
  AlertProps,
  CardProgress,
  VerticalSpace,
} from '@brokerbay/design-components';
import { MemberNumberState } from '@bb/users';
import { Link, Text } from 'components';
import * as React from 'react';
import styled from 'styled-components';
import { SALES_EMAIL, SUPPORT_EMAIL } from 'utils/constants';
import { AgentVerificationPageState } from '../types';

const SALES_LINK = <Link href={`mailto:${SALES_EMAIL}`}>{SALES_EMAIL}</Link>;
const SUPPORT_LINK = (
  <Link href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</Link>
);

export interface OnboardingAgentVerificationWrapperProps {
  pageState: AgentVerificationPageState;
  memberNumberState?: MemberNumberState;
  email?: string;
  error?: string | React.ReactNode;
  orgName?: string;
  authUrl?: string;
}

export const OnboardingAgentVerificationWrapper: React.FC<
  OnboardingAgentVerificationWrapperProps
> = ({
  pageState,
  memberNumberState,
  email,
  error,
  orgName,
  authUrl,
  children,
}) => {
  const alertProps: Pick<AlertProps, 'message' | 'type'> = {
    message: '',
    type: 'error',
  };
  let footer = null;
  if (error) {
    alertProps.message = error;
    alertProps.type = 'error';
  } else if (pageState === 'verificationSent') {
    alertProps.message = `A verification email has been sent to ${email}. Please click the link in the email to continue`;
    alertProps.type = 'info';
    footer = <CardProgress percent={(2 / 6) * 100} />;
  } else if (pageState === 'wrongOrg') {
    alertProps.message = (
      <>
        It looks like your member ID is not tied to your brokerage. Please
        contact {SUPPORT_LINK} for assistance.
      </>
    );
    alertProps.type = 'warning';
  } else if (['contactSales', 'contactOffice'].includes(pageState)) {
    switch (memberNumberState) {
      case MemberNumberState.active:
        alertProps.message = `This member number / ID already appears to have an active BrokerBay account with ${orgName}. Try logging in or contact your office for assistance.`;
        alertProps.type = 'warning';
        break;
      case MemberNumberState.deactivated:
        alertProps.message =
          'The account associated with this member ID appears to be deactivated. Please contact your office for assistance.';
        alertProps.type = 'warning';
        break;
      case MemberNumberState.none:
        alertProps.message = (
          <Text>
            We can&#39;t seem to find your member ID in our database. To get
            access, please contact our sales team at {SALES_LINK}
          </Text>
        );
        alertProps.type = 'error';
        break;
      case MemberNumberState.roster:
        alertProps.message = (
          <Text>
            Your brokerage doesn&#39;t have access. To get access, please
            contact our sales team at {SALES_LINK}
          </Text>
        );
        alertProps.type = 'error';
        break;
      default:
        alertProps.message = '';
    }

    if (pageState === 'contactOffice') {
      footer = (
        <StyledFlexDiv>
          <CenteredLink
            disabled={!authUrl}
            href={`${authUrl}/new-password`}
            data-cy="forgotPassword-link"
          >
            Forgot Password
          </CenteredLink>
        </StyledFlexDiv>
      );
    }
  }

  return (
    <VerticalSpace>
      {alertProps.message && (
        <Alert data-cy="agentVerification-alert" {...alertProps} />
      )}
      {children}
      {footer}
    </VerticalSpace>
  );
};

const StyledFlexDiv = styled.div`
  display: flex;
`;

const CenteredLink = styled(Link)`
  margin: auto;
`;
